import { memo, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { getActiveWorkspaceKey } from "../../../nav/workspaceRegistry";
import ShellIconNavItem from "./ShellIconNavItem";

export function workspaceExpandKey(wsKey) {
  return `ws:${wsKey}`;
}

/**
 * Secondary module bar under main Navbar.
 * Modules and workspace hubs (Reports / Settings / …) both open section flyouts.
 */
function AdminSecondaryNav({
  modules = [],
  workspaceItems = [],
  expandedKey,
  activeTabKey = "",
  pathname = "",
  search = "",
  onModuleClick,
  onChildClick,
  onWorkspaceClick,
  onWorkspaceSectionClick,
  getModuleEnabled,
}) {
  const barRef = useRef(null);
  const tileRefs = useRef({});
  const [flyoutPos, setFlyoutPos] = useState(null);

  const expandedModule = modules.find((m) => m.name === expandedKey);
  const expandedWorkspace = workspaceItems.find(
    (ws) => expandedKey === workspaceExpandKey(ws.key)
  );
  const flyoutChildren = Array.isArray(expandedWorkspace?.children)
    ? expandedWorkspace.children
    : Array.isArray(expandedModule?.children)
      ? expandedModule.children
      : [];
  const flyoutTitle = expandedWorkspace?.label || expandedModule?.name || "";
  const flyoutAnchorKey = expandedWorkspace
    ? workspaceExpandKey(expandedWorkspace.key)
    : expandedModule?.name || "";

  useEffect(() => {
    if (!expandedKey) return undefined;
    const onDoc = (e) => {
      if (barRef.current && !barRef.current.contains(e.target)) {
        const flyout = e.target.closest?.(".pace-shell-flyout");
        if (!flyout) onModuleClick?.(null);
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [expandedKey, onModuleClick]);

  useLayoutEffect(() => {
    if (!expandedKey || !flyoutChildren.length || !flyoutAnchorKey) {
      setFlyoutPos(null);
      return undefined;
    }
    const el = tileRefs.current[flyoutAnchorKey];
    if (!el) {
      setFlyoutPos(null);
      return undefined;
    }
    const place = () => {
      const rect = el.getBoundingClientRect();
      const width = 220;
      let left = rect.left;
      if (left + width > window.innerWidth - 8) {
        left = Math.max(8, rect.right - width);
      }
      setFlyoutPos({
        top: rect.bottom + 4,
        left,
        width,
      });
    };
    place();
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    return () => {
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
    };
  }, [expandedKey, flyoutAnchorKey, flyoutChildren.length]);

  const activeWorkspaceKey = useMemo(
    () => getActiveWorkspaceKey(pathname, search),
    [pathname, search]
  );

  const moduleParam = useMemo(() => {
    const params = new URLSearchParams(
      String(search || "").startsWith("?")
        ? String(search).slice(1)
        : String(search || "")
    );
    return params.get("module") || "";
  }, [search]);

  // Deep-linked workspace features keep the workspace tile active — not the module.
  const isModuleActive = (item) =>
    !activeWorkspaceKey &&
    (pathname === item.to || pathname.startsWith(`${item.to}/`));

  const flyout =
    flyoutChildren.length > 0 && flyoutPos && flyoutTitle
      ? createPortal(
          <div
            className="pace-shell-flyout"
            role="menu"
            aria-label={`${flyoutTitle} sections`}
            style={{
              position: "fixed",
              top: flyoutPos.top,
              left: flyoutPos.left,
              width: flyoutPos.width,
              zIndex: 10040,
            }}
          >
            <p className="pace-shell-flyout-title">{flyoutTitle}</p>
            <div className="pace-shell-flyout-list">
              {flyoutChildren.map((child) => {
                const childActive = expandedWorkspace
                  ? activeWorkspaceKey === expandedWorkspace.key &&
                    moduleParam === child.key
                  : isModuleActive(expandedModule) && activeTabKey === child.key;
                return (
                  <button
                    key={child.key}
                    type="button"
                    role="menuitem"
                    className={`pace-shell-flyout-item ${
                      childActive ? "is-active" : ""
                    }`}
                    onClick={() => {
                      if (expandedWorkspace) {
                        onWorkspaceSectionClick?.(expandedWorkspace, child);
                      } else {
                        onChildClick?.(expandedModule, child);
                      }
                    }}
                  >
                    {child.label}
                  </button>
                );
              })}
            </div>
          </div>,
          document.body
        )
      : null;

  return (
    <nav
      ref={barRef}
      className="pace-shell-secondary"
      aria-label="Admin modules"
    >
      <ul className="pace-shell-secondary-list">
        {modules.map((item) => {
          const enabled = getModuleEnabled ? getModuleEnabled(item) : true;
          const active = isModuleActive(item) || expandedKey === item.name;
          return (
            <li
              key={item.name}
              className="pace-shell-secondary-item"
              ref={(node) => {
                if (node) tileRefs.current[item.name] = node;
                else delete tileRefs.current[item.name];
              }}
            >
              <ShellIconNavItem
                icon={item.icon}
                label={item.tileLabel || item.name}
                title={item.name}
                orientation="horizontal"
                tone="admin"
                active={active}
                disabled={!enabled}
                onClick={() => onModuleClick?.(item)}
              />
            </li>
          );
        })}

        {workspaceItems.length > 0 ? (
          <li className="pace-shell-secondary-sep" aria-hidden="true" />
        ) : null}

        {workspaceItems.map((ws) => {
          const wsKey = workspaceExpandKey(ws.key);
          const active =
            activeWorkspaceKey === ws.key || expandedKey === wsKey;
          return (
            <li
              key={ws.key}
              className="pace-shell-secondary-item"
              ref={(node) => {
                if (node) tileRefs.current[wsKey] = node;
                else delete tileRefs.current[wsKey];
              }}
            >
              <ShellIconNavItem
                icon={ws.icon}
                label={ws.label}
                title={ws.label}
                orientation="horizontal"
                tone="admin"
                active={active}
                onClick={() => onWorkspaceClick?.(ws)}
              />
            </li>
          );
        })}
      </ul>
      {flyout}
    </nav>
  );
}

export default memo(AdminSecondaryNav);
